import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const leftAnim = useScrollAnimation({ threshold: 0.15 });
  const rightAnim = useScrollAnimation({ threshold: 0.15 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6f9po8r225iqdhs0510', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactItems = [
    {
      icon: 'ri-map-pin-line',
      label: 'Address',
      value: 'Luisa St., Corner Mangga St., Juna\nSubdivision Ecoland, Matina, Davao City'
    },
    {
      icon: 'ri-phone-line',
      label: 'Phone',
      value: '+63 (939) 939 8250'
    },
    {
      icon: 'ri-mail-line',
      label: 'Email',
      value: 'southdevhomedepo2020@gmail.com'
    },
    {
      icon: 'ri-time-line',
      label: 'Hours',
      value: 'Mon - Sat: 8:00 AM - 5:00 PM'
    }
  ];

  return (
    <section id="contact" className="py-14 lg:py-32 bg-[#2C2C2C]">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div
            ref={leftAnim.ref}
            className={`transition-all duration-700 ${leftAnim.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 lg:mb-6">
              Get In Touch
            </h2>
            <p className="text-white/80 text-sm lg:text-lg mb-8 lg:mb-12 leading-relaxed">
              Have questions about our products or services? Our team is here to help you find the perfect solutions for your home improvement projects.
            </p>

            <div className="space-y-5 lg:space-y-8">
              {contactItems.map((item, i) => (
                <div
                  key={item.icon}
                  className={`flex items-start gap-3 lg:gap-4 transition-all duration-600 ${leftAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${300 + i * 120}ms` }}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#FF6600] text-xl lg:text-2xl`}></i>
                  </div>
                  <div className="text-white">
                    <div className="font-semibold mb-0.5 lg:mb-1 text-sm lg:text-base">{item.label}</div>
                    <div className="text-white/80 whitespace-pre-line text-xs lg:text-sm">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            ref={rightAnim.ref}
            className={`transition-all duration-700 delay-200 ${rightAnim.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-4 lg:space-y-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full bg-transparent border border-white/30 text-white placeholder-white/60 px-4 lg:px-6 py-3 lg:py-4 rounded-lg focus:outline-none focus:border-[#FF6600] transition-colors duration-200 text-sm lg:text-base"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full bg-transparent border border-white/30 text-white placeholder-white/60 px-4 lg:px-6 py-3 lg:py-4 rounded-lg focus:outline-none focus:border-[#FF6600] transition-colors duration-200 text-sm lg:text-base"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your Phone"
                    required
                    className="w-full bg-transparent border border-white/30 text-white placeholder-white/60 px-4 lg:px-6 py-3 lg:py-4 rounded-lg focus:outline-none focus:border-[#FF6600] transition-colors duration-200 text-sm lg:text-base"
                  />
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full bg-transparent border border-white/30 text-white placeholder-white/60 px-4 lg:px-6 py-3 lg:py-4 rounded-lg focus:outline-none focus:border-[#FF6600] transition-colors duration-200 resize-none text-sm lg:text-base"
                  ></textarea>
                  <div className="text-white/60 text-xs lg:text-sm mt-1 lg:mt-2 text-right">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF6600] text-white py-3 lg:py-4 rounded-lg hover:bg-[#E55A00] transition-all duration-200 font-semibold text-sm lg:text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-white px-4 lg:px-6 py-3 lg:py-4 rounded-lg animate-fadeInUp text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-white px-4 lg:px-6 py-3 lg:py-4 rounded-lg animate-fadeInUp text-sm">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
